
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

type SimilarProperty = {
  id: string;
  address: string;
  city: string;
  state: string;
  price: number;
  bedrooms: number;
  bathrooms: number;
  square_feet: number;
  image_url: string | null;
  seller_name: string | null;
  seller_business: string | null;
  created_at: string;
  similarity_score: number;
}

interface UseSimilarPropertiesProps {
  currentPropertyId: string;
  city: string;
  price: number;
  bedrooms: number;
  bathrooms: number;
  propertyType?: string | null;
}

export const useSimilarProperties = ({
  currentPropertyId,
  city,
  price,
  bedrooms,
  bathrooms,
  propertyType
}: UseSimilarPropertiesProps) => {
  const { toast } = useToast();
  const [similarProperties, setSimilarProperties] = useState<SimilarProperty[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const calculateSimilarityScore = (property: any) => {
    let score = 0;
    
    // City match (highest priority - required)
    if (property.city.toLowerCase() === city.toLowerCase()) {
      score += 100;
    } else {
      return 0; // Skip if not same city
    }
    
    // Price similarity (±25% range)
    const priceMin = price * 0.75;
    const priceMax = price * 1.25;
    if (property.price >= priceMin && property.price <= priceMax) {
      score += 50;
      // Bonus for closer price match
      const priceDiff = Math.abs(property.price - price) / price;
      score += (1 - priceDiff) * 20;
    }
    
    // Bedroom similarity (same or ±1)
    const bedroomDiff = Math.abs(property.bedrooms - bedrooms);
    if (bedroomDiff === 0) {
      score += 30;
    } else if (bedroomDiff === 1) {
      score += 15;
    }
    
    // Bathroom similarity (same or ±1)
    const bathroomDiff = Math.abs(property.bathrooms - bathrooms);
    if (bathroomDiff === 0) {
      score += 20;
    } else if (bathroomDiff <= 1) {
      score += 10;
    }
    
    // Property type match
    if (propertyType && property.property_type === propertyType) {
      score += 15;
    }
    
    return score;
  };

  const fetchSimilarProperties = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Fetch properties first
      const { data: propertiesData, error: propertiesError } = await supabase
        .from('properties')
        .select(`
          id,
          address,
          city,
          state,
          price,
          bedrooms,
          bathrooms,
          square_feet,
          image_url,
          property_type,
          created_at,
          seller_id
        `)
        .eq('status', 'active')
        .neq('id', currentPropertyId);
      
      if (propertiesError) {
        console.error("Error fetching properties:", propertiesError);
        setError("Failed to load similar properties");
        return;
      }
      
      if (!propertiesData || propertiesData.length === 0) {
        setSimilarProperties([]);
        return;
      }

      // Get unique seller IDs
      const sellerIds = [...new Set(propertiesData.map(p => p.seller_id))];
      
      // Fetch profiles for all sellers using secure function
      const { data: profilesData, error: profilesError } = await (supabase as any)
        .rpc('get_seller_profiles_for_properties', { seller_ids: sellerIds });
      
      if (profilesError) {
        console.error("Error fetching profiles:", profilesError);
        // Continue without seller info if profiles fail
      }
      
      // Create a map of seller profiles for quick lookup
      const profilesMap = new Map();
      profilesData?.forEach(profile => {
        profilesMap.set(profile.id, profile);
      });
      
      // Calculate similarity scores and combine data
      const scoredProperties = propertiesData
        .map(property => {
          const score = calculateSimilarityScore(property);
          const profile = profilesMap.get(property.seller_id);
          
          return {
            ...property,
            seller_name: profile?.full_name || null,
            seller_business: profile?.business_name || null,
            similarity_score: score
          };
        })
        .filter(property => property.similarity_score > 0) // Only include city matches
        .sort((a, b) => b.similarity_score - a.similarity_score) // Sort by highest similarity
        .slice(0, 4); // Limit to top 4 results
      
      setSimilarProperties(scoredProperties);
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
      setError(errorMessage);
      console.error('Error fetching similar properties:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (currentPropertyId && city) {
      fetchSimilarProperties();
    }
  }, [currentPropertyId, city, price, bedrooms, bathrooms, propertyType]);

  // Set up real-time subscriptions for property changes with Safari protection
  useEffect(() => {
    let channel: ReturnType<typeof supabase.channel> | null = null;

    try {
      channel = supabase
        .channel('similar-properties-updates')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'properties'
          },
          () => {
            fetchSimilarProperties();
          }
        )
        .subscribe();
    } catch (error) {
      console.warn('Failed to subscribe to similar properties (Safari security restriction?):', error);
    }

    return () => {
      try {
        if (channel) supabase.removeChannel(channel);
      } catch (error) {
        console.warn('Error cleaning up similar properties subscription:', error);
      }
    };
  }, [currentPropertyId, city, price, bedrooms, bathrooms, propertyType]);

  return {
    similarProperties,
    isLoading,
    error,
    refetch: fetchSimilarProperties
  };
};
